import { FormEvent, useEffect, useMemo, useState } from "react";
import PageLayout from "../components/PageLayout";
import { apiUrl } from "../services/api";

const TOKEN_STORAGE_KEY = "checkride.admin.jwt";
const RETURN_HASH_KEY = "checkride.admin.return_hash";

const AdminSignInPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const savedToken = useMemo(() => localStorage.getItem(TOKEN_STORAGE_KEY), []);

  useEffect(() => {
    setEmail("");
    setPassword("");
  }, []);

  const redirectAfterSignIn = () => {
    const returnHash = sessionStorage.getItem(RETURN_HASH_KEY) || "#/admin";
    sessionStorage.removeItem(RETURN_HASH_KEY);
    if (returnHash !== window.location.hash) {
      window.location.hash = returnHash;
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setStatus(null);
    setSubmitting(true);

    try {
      const response = await fetch(apiUrl("/users/sign_in").toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({ user: { email, password } })
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.error || payload?.message || "Sign in failed";
        throw new Error(message);
      }

      const authHeader = response.headers.get("Authorization") || response.headers.get("authorization");
      if (!authHeader) {
        throw new Error("Missing Authorization header in response");
      }

      localStorage.setItem(TOKEN_STORAGE_KEY, authHeader);
      setStatus("Signed in.");
      setPassword("");
      redirectAfterSignIn();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Sign in failed";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <PageLayout route="admin">
      <section className="panel panel-tight">
        <h2>Admin Sign In</h2>
        <p>Sign in to manage metadata.</p>
        {savedToken && <p className="status">Token is already stored.</p>}
        <form onSubmit={handleSubmit} className="form-stack" autoComplete="on">
          <label className="form-field">
            <span>Email</span>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              required
            />
          </label>
          <label className="form-field">
            <span>Password</span>
            <input
              type="password"
              name="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <div className="form-actions">
            <button type="submit" disabled={submitting}>
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </div>
        </form>
        {error && <p className="alert">{error}</p>}
        {status && <p className="status">{status}</p>}
      </section>
    </PageLayout>
  );
};

export default AdminSignInPage;
